"use client";

import {
  isMessageOverEvent,
  MessageOverEvent,
} from "@/modules/types/conversation/message";
import {
  isNotificationMessageDto,
  NotificationMessageDto,
} from "@/modules/types/notification/notification";
import { Button } from "@headlessui/react";
import { useRouter } from "next/navigation";
import {
  CustomContentProps,
  MaterialDesignContent,
  SnackbarContent,
  useSnackbar,
} from "notistack";
import React from "react";
import styled from "styled-components";
import { useClientTranslations } from "@/lib/translate";
import { routes } from "@/lib/utils/routes";
import { Notification01Icon } from "hugeicons-react";

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  "&.notistack-MuiContent-success": {
    backgroundColor: "var(--background)",
    borderRadius: "10px",
    borderColor: "#2D7738",
    borderWidth: "1px",
    color: "#2D7738",
    "@media (prefers-color-scheme: dark)": {
      borderColor: "#4ade80",
      color: "#4ade80",
    },
  },
  "&.notistack-MuiContent-error": {
    backgroundColor: "var(--background)",
    borderRadius: "10px",
    borderColor: "#970C0C",
    borderWidth: "1px",
    color: "#970C0C",
    "@media (prefers-color-scheme: dark)": {
      borderColor: "#f87171",
      color: "#f87171",
    },
  },
  "&.notistack-MuiContent-warning": {
    backgroundColor: "var(--background)",
    borderRadius: "10px",
    borderColor: "#F2B300",
    borderWidth: "1px",
    color: "#F2B300",
    "@media (prefers-color-scheme: dark)": {
      borderColor: "#fbbf24",
      color: "#fbbf24",
    },
  },
  "&.notistack-MuiContent-info": {
    backgroundColor: "var(--background)",
    borderRadius: "10px",
    borderColor: "#007DFF",
    borderWidth: "1px",
    color: "#007DFF",
    "@media (prefers-color-scheme: dark)": {
      borderColor: "#60a5fa",
      color: "#60a5fa",
    },
  },
  "&.notistack-MuiContent-default": {
    backgroundColor: "var(--background)",
    borderRadius: "10px",
    borderColor: "#6c757d",
    borderWidth: "1px",
    color: "var(--foreground)",
    "@media (prefers-color-scheme: dark)": {
      borderColor: "#4b5563",
    },
  },
}));

export default StyledMaterialDesignContent;

interface NotificationCustomContentProps extends Partial<CustomContentProps> {
  notification: NotificationMessageDto | MessageOverEvent;
}

const StyledSnackbarContent = styled(SnackbarContent)(() => ({
  backgroundColor: "var(--background)",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  boxShadow:
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  padding: "16px",
  minWidth: "300px",
  maxWidth: "400px",
  "@media (prefers-color-scheme: dark)": {
    borderColor: "#374151",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.2)",
  },
}));

const NotificationContainer = styled.div(() => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
}));

const NotificationHeader = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "4px",
}));

const NotificationTitle = styled.div(() => ({
  fontSize: "14px",
  fontWeight: "600",
  color: "var(--foreground)",
  lineHeight: "1.4",
}));

const NotificationContent = styled.div(() => ({
  fontSize: "13px",
  color: "#6b7280",
  lineHeight: "1.5",
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  "@media (prefers-color-scheme: dark)": {
    color: "#9ca3af",
  },
}));

const NotificationAction = styled.div(() => ({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "4px",
}));

const StyledButton = styled(Button)(() => ({
  backgroundColor: "#9333ea",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  padding: "6px 12px",
  fontSize: "12px",
  fontWeight: "500",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#a855f7",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
}));

const CloseButton = styled.button(() => ({
  backgroundColor: "transparent",
  border: "none",
  color: "#9ca3af",
  cursor: "pointer",
  padding: "2px",
  borderRadius: "4px",
  fontSize: "16px",
  lineHeight: "1",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    color: "var(--foreground)",
    backgroundColor: "#f3f4f6",
  },
  "@media (prefers-color-scheme: dark)": {
    "&:hover": {
      backgroundColor: "#374151",
    },
  },
}));

const IconWrapper = styled.div(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  color: "#9333ea",
  flexShrink: "0",
}));

export const NotificationCustomContent = React.forwardRef<
  HTMLDivElement,
  NotificationCustomContentProps
>((props, ref) => {
  const { t } = useClientTranslations("notification");
  const { id, notification, ...other } = props;
  const router = useRouter();
  const { closeSnackbar } = useSnackbar();

  let title = "";
  let content = "";
  let action: React.ReactNode = null;

  if (isNotificationMessageDto(notification)) {
    const noti = notification as NotificationMessageDto;
    title = noti.title || t("newNotificationTitle");
    content = noti.content || "";
    if (noti.actionUrl) {
      action = (
        <StyledButton
          onClick={() => {
            closeSnackbar(id);
            if (noti.actionUrl) {
              router.push(noti.actionUrl as string);
            } else {
              window.focus();
            }
          }}
        >
          {t("view")}
        </StyledButton>
      );
    }
  } else if (isMessageOverEvent(notification)) {
    const msg = notification as MessageOverEvent;
    title = `${msg.senderName} ${t("newMessageSubject")}`;
    content = msg.content;
    action = (
      <StyledButton
        onClick={() => {
          closeSnackbar(id);
          router.push(`${routes.candidate.message}/${msg.conversationId}`);
        }}
      >
        {t("view")}
      </StyledButton>
    );
  }

  return (
    <StyledSnackbarContent ref={ref} role="alert" {...other}>
      <NotificationContainer>
        <NotificationHeader>
          <NotificationTitle>{title}</NotificationTitle>
          <CloseButton onClick={() => closeSnackbar(id)}>×</CloseButton>
        </NotificationHeader>
        <NotificationContent>
          <IconWrapper>
            <Notification01Icon />
          </IconWrapper>
          <div>{content}</div>
        </NotificationContent>
        {action && <NotificationAction>{action}</NotificationAction>}
      </NotificationContainer>
    </StyledSnackbarContent>
  );
});

NotificationCustomContent.displayName = "NotificationCustomContent";
